import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { Raincheck } from "../Raincheck/Raincheck";
import { RainchecksList_customer$key } from "./__generated__/RainchecksList_customer.graphql";

type Props = {
  fragmentRef: RainchecksList_customer$key | null;
  onSelectRaincheck?: (raincheckId: string) => void;
  appliedRaincheckIds?: string[];
  activeOnly?: boolean;
  showTotalBalance?: boolean;
};

export const RainchecksList = ({
  fragmentRef,
  onSelectRaincheck,
  appliedRaincheckIds,
  activeOnly,
  showTotalBalance,
}: Props) => {
  const data = useFragment(RainchecksListFragmentString, fragmentRef);
  const { currencySymbol } = useGetClubCurrency();

  const availableRainchecks =
    data?.availableRainchecks?.edges?.filter(
      (edge) =>
        !appliedRaincheckIds?.includes(edge?.node?.id ?? "") &&
        (!activeOnly || edge?.node?.squareGiftCard?.status === "ACTIVE")
    ) ?? [];
  const usedRainchecks = data?.usedRainchecks?.edges;
  const totalBalance = availableRainchecks.reduce(
    (total, raincheck) =>
      total + (raincheck?.node?.squareGiftCard?.balance ?? 0),
    0
  );

  return (
    <VStack gap={6} pb={4} px={6}>
      {showTotalBalance ? (
        <HStack pt={8} justifyContent="space-between" alignItems="center">
          <Text variant="mainTitle2">{t`Rainchecks`}</Text>
          <HStack gap={2} alignItems="center">
            <Text variant="mainBaseSemiBold">{t`Total balance`}</Text>
            <Text variant="mainBaseSemiBold">
              {currencySymbol}
              {ConvertCentsToDollars(totalBalance)}
            </Text>
          </HStack>
        </HStack>
      ) : null}
      <VStack gap={2}>
        {availableRainchecks.map((raincheck) => (
          <Raincheck
            key={raincheck?.node?.id}
            fragmentRef={raincheck?.node ?? null}
            onPress={() => onSelectRaincheck?.(raincheck?.node?.id ?? "")}
          />
        ))}
      </VStack>

      {usedRainchecks?.length && usedRainchecks?.length > 0 ? (
        <VStack gap={2}>
          <Text
            variant="mainTiny"
            color="text-secondary"
            textTransform="uppercase"
          >
            {t`Used Rainchecks`}
          </Text>
          {usedRainchecks?.map((raincheck) => (
            <Raincheck
              key={raincheck?.node?.id}
              fragmentRef={raincheck?.node ?? null}
            />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
};

export const RainchecksListFragmentString = graphql`
  fragment RainchecksList_customer on Customer
  @argumentDefinitions(
    redeemableAtFacilityId: { type: "ID", defaultValue: null }
  ) {
    availableRainchecks: rainchecks(
      expired: false
      giftCardIsActive: true
      hasNonZeroBalance: true
      redeemableAtFacilityId: $redeemableAtFacilityId
    ) {
      edges {
        node {
          ...Raincheck_raincheck
          id
          squareGiftCard {
            id
            balance
            status
          }
        }
      }
    }

    usedRainchecks: rainchecks(hasNonZeroBalance: false) {
      edges {
        node {
          ...Raincheck_raincheck
          id
          squareGiftCard {
            id
            balance
            status
          }
        }
      }
    }
  }
`;
